import { ClipboardEvent, useEffect, useState } from 'react';
import { useAddCardMutation } from '../../../../../redux/services/CardsApi';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useGetBanksQuery } from '../../../../../redux/services/BanksApi';
import { Button } from '../../../../../components/Button/Button';
import { Loading } from '../../../../../components/Loading/Loading';
import { ModalDefault } from '../../../../../components/ModalDefault/ModalDefault';
import { ControlledInput } from '../../../../../components/ControlledInput/ControlledInput';
import { Selector, type TItem } from '../../../../../components/Selector/Selector';
import { parseStringToNumber } from '../../../../../utils/functions/parseNumber';
import { type TBanksModel } from '../../../../../redux/types/BanksTypes';
import styles from './styles.module.css';
import { IInputs } from './types';
import { paymentNotificationTypeOptions, paymentTypeOptions, transformFunction } from './utils';
import { phoneRegex } from 'utils/constants/text';
import { pushNotify } from 'components/Notifications';
import { useGetCurrenciesQuery } from 'redux/services/CurrencyApi';
import { isCurrencyAmount, sanitizeCurrencyAmount } from 'utils/functions/currencyAmount';
import { FOREIGN_METHODS, isForeignMethod } from 'utils/functions/paymentTypeLabel';
import { AUTO_DISABLE_HINT, autoDisableFromInput } from 'utils/functions/autoDisable';

export const AddCard = () => {
  const formMethods = useFormContext<IInputs>();
  const { handleSubmit, setError, reset, watch, getValues, setValue } = formMethods;
  const [activeModal, setActiveModal] = useState<boolean>(false);
  const [cardExist, setCardExist] = useState<boolean>(false);
  const [addCard] = useAddCardMutation();
  const watchMethod = watch('payment_type');
  const currencyCode = watch('currency_code');
  const { data: currencies = [] } = useGetCurrenciesQuery('requisite');
  const selectedCurrency = currencies.find((currency) => currency.code === currencyCode);
  const paymentMethods = selectedCurrency?.payment_methods || [];
  const availablePaymentTypeOptions = paymentTypeOptions.filter((item) => {
    if (item.id === 'C2C/SBP') {
      return paymentMethods.includes('C2C') && paymentMethods.includes('SBP');
    }
    if (isForeignMethod(item.id)) {
      // the method is enabled on RUB (the order currency), but the card itself is in the method's card currency
      return (
        currencyCode === FOREIGN_METHODS[item.id].cardCurrency &&
        currencies.some((currency) => currency.payment_methods.includes(item.id))
      );
    }
    return paymentMethods.includes(item.id);
  });
  const isCardNumberMethod =
    watchMethod === 'C2C' || watchMethod === 'SBP' || watchMethod === 'C2C/SBP' || isForeignMethod(watchMethod);

  const payment_notification_type = watch('payment_notification_type');

  const { data: banks } = useGetBanksQuery(
    { payment_notification_type, currency_code: currencyCode, payment_method: watchMethod },
    {
      skip: !payment_notification_type || !currencyCode || !watchMethod,
    },
  );

  const onSubmit = async (data: IInputs) => {
    const scale = selectedCurrency?.amount_scale ?? 6;
    const invalidAmount = [data.limit, data.min_income, data.max_income].find(
      (value) => !isCurrencyAmount(value, scale),
    );
    if (invalidAmount) {
      pushNotify(`Для ${currencyCode} допустимо не более ${scale} знаков после запятой`, 'Ошибка');
      return;
    }
    if (!data.payment_notification_type || data.payment_notification_type === '') {
      setError('payment_notification_type', {
        type: 'minLength',
        message: 'You need to select a payment notification type',
      });
      return;
    }

    if (!data.bankName || data.bankName === '') {
      setError('bankName', { type: 'minLength', message: 'You need to select a bank' });
      return;
    }

    const selectedBank = banks?.results.find((bank) => bank.name === data.bankName);
    if (!selectedBank) {
      setError('bankName', { type: 'manual', message: 'Выбранный банк больше недоступен' });
      return;
    }

    if (phoneRegex.test(data.phone_number) === false && (data.payment_type === 'SBP' || data.payment_type === 'C2C/SBP')) {
      pushNotify('Некорректный номер телефона', 'Ошибка');
      return;
    }

    if (data.tag.length > 32) {
      pushNotify('Тег не должен превышать 32 символов', 'Ошибка');
      setError('tag', { type: 'manual', message: 'Тег не должен превышать 32 символов' });
      return;
    }

    const newCard = {
      currency_code: data.currency_code,
      account_number: data.cardNumber ? data.cardNumber : data.accountNumber,
      account_number_second: data.accountNumber,
      tag: data.tag,
      payment_type: data.payment_type,
      active: true,
      phone_number: data.phone_number,
      total_limit: parseStringToNumber(data.limit),
      fio: data.name,
      description: data.description,
      min_income: Number(data.min_income) ?? 0,
      max_income: Number(data.max_income) ?? 0,
      instant_active_orders: Number(data.instant_active_orders) ?? 0,
      auto_disable_after_successes: autoDisableFromInput(data.auto_disable_after_successes),
      auto_disable_after_timeouts: autoDisableFromInput(data.auto_disable_after_timeouts),
      bank: Number(selectedBank.id),
    };
    await addCard(newCard)
      .unwrap()
      .then(() => {
        setActiveModal(false);
        reset();
      })
      .catch((error) => {
        if (error.data.non_field_errors) {
          error.data.non_field_errors.forEach((e: string) => {
            pushNotify(e, 'Ошибка');
          });
          return;
        }
        if (error.data.tag) {
          error.data.tag.forEach((e: string) => {
            pushNotify(e, 'Ошибка');
          });
          return;
        }
        if (error.data.phone_number) {
          error.data.phone_number.forEach((e: string) => {
            pushNotify(e, 'Ошибка');
          });
          return;
        }
        if (error.status === 400) {
          setActiveModal(false);
          setCardExist(true);
        }
      });
  };

  useEffect(() => {
    switch (watchMethod) {
      case 'SBP':
        formMethods.resetField('accountNumber');
        break;
      case 'C2C/SBP':
        formMethods.resetField('accountNumber');
        break;
      case 'C2C':
        formMethods.resetField('accountNumber');
        formMethods.setValue('phone_number', '');
        break;
      case 'R_UZS':
      case 'R_TJS':
        formMethods.resetField('accountNumber');
        formMethods.setValue('phone_number', '');
        break;
      case 'TRANSFER':
        formMethods.resetField('cardNumber');
        formMethods.resetField('phone_number');
    }
  }, [watchMethod]);

  useEffect(() => {
    formMethods.resetField('payment_type');
    formMethods.resetField('bankName');
  }, [currencyCode]);

  useEffect(() => {
    // RUB cards switch off after 1 success / 2 timeouts in a row by default; other currencies have no limit
    const isRub = currencyCode === 'RUB';
    setValue('auto_disable_after_successes', isRub ? '1' : '');
    setValue('auto_disable_after_timeouts', isRub ? '2' : '');
  }, [currencyCode, setValue]);

  useEffect(() => {
    setValue('bankName', banks?.results[0]?.name || '');
  }, [banks]);

  return (
    <FormProvider {...formMethods}>
      <div>
        <Button
          text={'Добавить реквизиты'}
          type={'button'}
          action={() => {
            setActiveModal(true);
          }}
          style={{ position: 'absolute', right: '0', top: '0' }}
        />
        <ModalDefault
          title={'Новые реквизиты'}
          titleMargin={'12px'}
          active={activeModal}
          setActive={setActiveModal}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <ControlledInput
              title={'Название реквизита (тег)'}
              required={true}
              placeholder={'Введите название'}
              name={'tag'}
              type={'text'}
              blockStyle={{ marginBottom: '8px' }}
              rules={{
                required: true,
                validate: (value) => value.trim() !== '',
              }}
              transform={(text: string | undefined) => {
                if (text) {
                  return text.slice(0, 32);
                }
                return text;
              }}
            />
            <div style={{ display: 'flex', width: '100%', gap: '8px', marginBottom: '-10px' }}>
              <Selector
                title={'Валюта'}
                placeholder={'Выберите валюту'}
                required={true}
                name={'currency_code'}
                items={currencies.map((currency) => ({ name: currency.code, id: currency.code }))}
                blockStyle={{ marginBottom: '16px', width: '100%' }}
              />
              <Selector
                title={'Метод оплаты'}
                placeholder={'Выберите метод оплаты'}
                required={true}
                name={'payment_type'}
                items={availablePaymentTypeOptions}
                disabled={!currencyCode}
                blockStyle={{ marginBottom: '16px', width: '100%' }}
                defaultValue={getValues('payment_type')}
              />
              <Selector
                title={'Тип сообщений'}
                placeholder={'Выберите тип'}
                required={true}
                name={'payment_notification_type'}
                items={paymentNotificationTypeOptions}
                blockStyle={{ marginBottom: '16px', width: '100%' }}
                defaultValue={getValues('payment_notification_type')}
              />
            </div>
            {isForeignMethod(watchMethod) && (
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>
                {FOREIGN_METHODS[watchMethod].hint}. Общий лимит и суммы платежа такой карты указываются в RUB.
              </p>
            )}
            <ControlledInput
              title={'ФИО'}
              placeholder={'Введите ФИО'}
              name={'name'}
              type={'text'}
              isOnlyletters
              required
              rules={{
                required: true,
                maxLength: 100,
                validate: (value) => value.trim() !== '' && /^[a-zA-Zа-яА-Я ]+$/.test(value),
              }}
              blockStyle={{ marginBottom: '12px' }}
            />
            <div style={{ display: 'flex', width: '100%', gap: '8px', marginBottom: '-10px' }}>
              <Selector
                title={'Наименование банка'}
                defaultValue={getValues('bankName')}
                name={'bankName'}
                placeholder={'Выберите банк'}
                required={true}
                disabled={!payment_notification_type}
                blockStyle={{ marginBottom: '16px', width: '100%' }}
                items={
                  banks?.results.map((bank: TBanksModel): TItem => {
                    return {
                      name: bank.name,
                      id: String(bank.id),
                    };
                  }) || []
                }
              />
              <ControlledInput
                title={'Номер карты'}
                placeholder={'XXXX XXXX XXXX XXXX'}
                name={'cardNumber'}
                type={'text'}
                disabled={!isCardNumberMethod}
                required={isCardNumberMethod}
                rules={{
                  required: isCardNumberMethod,
                  maxLength: 19,
                  minLength: 19,
                }}
                blockStyle={{ marginBottom: '16px', width: '100%' }}
                transform={(text: string | undefined) => {
                  if (text) {
                    return text
                      .replace(/\D/g, '')
                      .replace(/(\d{4})/g, '$1 ')
                      .trim()
                      .slice(0, 19);
                  }
                  return text;
                }}
                calculateNewPosition={(currentPosition: number, newPosition: number) => {
                  if (currentPosition === 5 || currentPosition === 10 || currentPosition === 15) {
                    return newPosition + 1;
                  }
                  return newPosition;
                }}
              />
            </div>
            <div style={{ display: 'flex', width: '100%', gap: '8px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}>
                <ControlledInput
                  title={'Номер счёта'}
                  placeholder={'Введите номер счёта'}
                  name={'accountNumber'}
                  type={'text'}
                   disabled={!(watchMethod === 'C2C' || watchMethod === 'SBP' || watchMethod === 'C2C/SBP' || watchMethod === 'TRANSFER')}
                   required={(watchMethod === 'C2C' || watchMethod === 'SBP' || watchMethod === 'C2C/SBP' || watchMethod === 'TRANSFER') && !!watchMethod}
                   rules={{
                       required: watchMethod === 'C2C' || watchMethod === 'SBP' || watchMethod === 'C2C/SBP' || watchMethod === 'TRANSFER',
                       maxLength: 20,
                       minLength: 20,
                   }}
                  blockStyle={{ width: '100%' }}
                  transform={(text: string | undefined) => {
                    if (text) {
                      return text
                      .replace(/[^0-9]/g, '')
                      .trim()
                      .slice(0, 20);
                    }
                    return text;
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Controller
                  rules={{
                    required:  watchMethod === 'SBP' || watchMethod === 'C2C/SBP',
                    minLength: 12,
                    maxLength: 12,
                  }}
                  render={({ field: { onChange, ref, value } }) => (
                    <>
                      <label style={{ fontSize: '14px' }}>
                        {(watchMethod === 'SBP' || watchMethod === 'C2C/SBP') && <span style={{ color: 'red' }}>*</span>} Номер
                        телефона
                      </label>
                      <input
                        type='text'
                        onChange={(e) => {
                          if (Number.isNaN(+e.target.value) || e.target.value.slice(-1) === ' ') {
                            return;
                          }
                          if (e.target.value.startsWith('+7')) {
                            onChange(e.target.value);
                          }
                        }}
                        onFocus={(e) => {
                          if (e.target.value === '') {
                            onChange('+7');
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '+7') {
                            onChange('');
                          }
                        }}
                        ref={ref}
                        value={value}
                        maxLength={12}
                        minLength={12}
                        className={styles.myInput}
                        placeholder={'Введите номер телефона'}
                        disabled={(watchMethod !== 'SBP' && watchMethod !== 'C2C/SBP')}
                        style={{
                          height: '36px',
                          width: '100%',
                          borderRadius: '2px',
                          border: 'none',
                          outline: '2px solid #dbe0f2',
                          margin: '4px 0 0',
                          padding: '12px',
                          background: (watchMethod !== 'SBP' && watchMethod !== 'C2C/SBP') ? '#E2E5F0' : 'white',
                        }}
                      />
                    </>
                  )}
                  name={'phone_number'}
                />
              </div>
            </div>
            <div style={{ display: 'flex', width: '100%', gap: '8px' }}>
              <ControlledInput
                title={'Общий лимит'}
                placeholder={'0'}
                name={'limit'}
                type={'text'}
                required
                rules={{ required: true, maxLength: 13 }}
                blockStyle={{ marginBottom: '12px', width: '100%' }}
                transform={(text: string | undefined) => {
                  if (text) {
                    return text
                      .replace(/[^0-9]/g, '')
                      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')
                      .slice(0, 13);
                  }
                  return text;
                }}
                calculateNewPosition={(
                  currentPosition: number,
                  newPosition: number,
                  valueLen: number,
                  value,
                ) => {
                  if (valueLen - 1 === currentPosition || valueLen === currentPosition) {
                    return valueLen;
                  }
                  const extra = value.replaceAll(' ', '').length % 3;
                  const positions: number[] = [extra > 0 ? extra + 1 : 4];
                  for (let i = 0; i < 4; i++) {
                    positions.push(positions[i] + 4);
                  }
                  if (positions.includes(currentPosition)) {
                    return newPosition + 1;
                  }
                  return newPosition;
                }}
              />
            </div>
            <div style={{ display: 'flex', width: '100%', gap: '8px', alignItems: 'flex-end' }}>
              <ControlledInput
                title={'Минимальная сумма платежа'}
                placeholder={'0'}
                name={'min_income'}
                type={'text'}
                blockStyle={{ marginBottom: '8px', width: '100%' }}
                transform={(text) => sanitizeCurrencyAmount(transformFunction(text), selectedCurrency?.amount_scale)}
              />
              <ControlledInput
                title={'Максимальная сумма платежа'}
                placeholder={'0'}
                name={'max_income'}
                required
                rules={{ required: true, min: 1 }}
                type={'text'}
                blockStyle={{ marginBottom: '8px', width: '100%' }}
                transform={(text) => sanitizeCurrencyAmount(transformFunction(text), selectedCurrency?.amount_scale)}
              />
            </div>
            <ControlledInput
              title={'Заявок одновременно не более'}
              placeholder={'0'}
              name={'instant_active_orders'}
              type={'text'}
              required
              blockStyle={{ marginBottom: '8px' }}
              rules={{ min: 1, required: true }}
              transform={(text: string | undefined) => {
                if (text) {
                  return text.replace(/[^0-9]/g, '');
                }
                return text;
              }}
            />
            <ControlledInput
              title={'Выключать после успешных заявок'}
              placeholder={AUTO_DISABLE_HINT}
              name={'auto_disable_after_successes'}
              type={'text'}
              blockStyle={{ marginBottom: '8px' }}
              rules={{ maxLength: 4 }}
              transform={(text: string | undefined) => (text ? text.replace(/[^0-9]/g, '').slice(0, 4) : text)}
            />
            <ControlledInput
              title={'Выключать после тайм-аутов подряд'}
              placeholder={AUTO_DISABLE_HINT}
              name={'auto_disable_after_timeouts'}
              type={'text'}
              blockStyle={{ marginBottom: '8px' }}
              rules={{ maxLength: 4 }}
              transform={(text: string | undefined) => (text ? text.replace(/[^0-9]/g, '').slice(0, 4) : text)}
            />
            <ControlledInput
              title={'Описание'}
              placeholder={'Введите описание'}
              name={'description'}
              type={'text'}
              blockStyle={{ marginBottom: '8px' }}
              transform={(text: string | undefined) => {
                if (text) {
                  return text.slice(0, 100);
                }
                return text;
              }}
            />
            <Button text={'Добавить'} type={'submit'} disabled={false} />
          </form>
        </ModalDefault>

        <ModalDefault
          title={'Ошибка'}
          titleMargin={'8px'}
          textMargin={'24px'}
          active={cardExist}
          setActive={setCardExist}
          text={'Эта карта уже была добавлена. Хотите добавить новую?'}
        >
          <Button
            action={() => {
              setCardExist(false);
            }}
            text={'Отмена'}
            filled={false}
            style={{ marginRight: '8px' }}
          />
          <Button
            action={() => {
              setCardExist(false);
              setActiveModal(true);
            }}
            text={'Добавить новую'}
          />
        </ModalDefault>
      </div>
    </FormProvider>
  );
};
